"use client";

import { useState, useEffect } from "react";
import { getApiUrl, getCurrentCustomer } from "@/lib/api";
import { useCart } from "./CartContext";
import { PaymentForm } from "@/components/PaymentForm";
import { ShoppingCart, X, Loader2, ChevronLeft, Minus, Plus, Trash2 } from "lucide-react";

type Step = "cart" | "customer" | "payment";

export function CartDrawer({
  businessSlug,
  open,
  onClose,
  acceptsPayments = true,
}: {
  businessSlug: string;
  open: boolean;
  onClose: () => void;
  acceptsPayments?: boolean;
}) {
  const { items, totalCents, totalCount, clearCart, removeItem, updateQuantity } = useCart();
  const [step, setStep] = useState<Step>("cart");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [checkoutAmountCents, setCheckoutAmountCents] = useState(0);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [variantStock, setVariantStock] = useState<Record<string, number>>({});

  // Fetch current stock when drawer opens so we can cap quantity in cart (variants + product-level when no variants)
  useEffect(() => {
    if (!open || !businessSlug) return;
    let cancelled = false;
    fetch(`${getApiUrl()}/api/public/business/${businessSlug}/products`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : { products: [] }))
      .then((data) => {
        if (cancelled || !Array.isArray(data.products)) return;
        const map: Record<string, number> = {};
        for (const p of data.products as { id: string; trackStock: boolean; stockQuantity?: number; variants: { id: string; stockQuantity?: number }[] }[]) {
          if (!p.trackStock) continue;
          if (p.variants?.length) {
            for (const v of p.variants) {
              if (v.stockQuantity != null) map[`${p.id}:${v.id}`] = v.stockQuantity;
            }
          } else {
            if (p.stockQuantity != null) map[`${p.id}:`] = p.stockQuantity;
          }
        }
        setVariantStock(map);
      })
      .catch(() => setVariantStock({}));
    return () => { cancelled = true; };
  }, [open, businessSlug]);

  const handleCheckoutClick = async () => {
    setCheckoutError(null);
    setCheckingAuth(true);
    try {
      const profile = await getCurrentCustomer();
      if (!profile) {
        const callbackUrl = typeof window !== "undefined" ? window.location.pathname + window.location.search : `/book/${businessSlug}`;
        window.location.href = `/auth/customer/login?callbackUrl=${encodeURIComponent(callbackUrl)}`;
        return;
      }
      setEmail(profile.email ?? "");
      setName(profile.name ?? "");
      setStep("customer");
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleCustomerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const em = email.trim().toLowerCase();
    if (!em) {
      setCheckoutError("Please enter your email.");
      return;
    }
    setSubmitting(true);
    setCheckoutError(null);
    try {
      const res = await fetch(`${getApiUrl()}/api/public/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          businessSlug,
          lineItems: items.map((i) => ({
            productId: i.productId,
            productVariantId: i.productVariantId ?? undefined,
            quantity: i.quantity,
          })),
          customer: { email: em, name: name.trim() || undefined },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCheckoutError(data.error ?? "Checkout failed. Please try again.");
        return;
      }
      setClientSecret(data.clientSecret);
      setCheckoutAmountCents(data.totalCents ?? totalCents);
      setOrderId(data.orderId ?? null);
      setStep("payment");
    } catch {
      setCheckoutError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentSuccess = () => {
    clearCart();
    setStep("cart");
    setClientSecret(null);
    onClose();
    const base = window.location.origin;
    const path = `/book/${businessSlug}/order-success${orderId ? `?orderId=${encodeURIComponent(orderId)}` : ""}`;
    window.location.href = base + path;
  };

  const handleBack = () => {
    if (step === "customer") {
      setStep("cart");
      setCheckoutError(null);
    } else if (step === "payment") {
      setStep("customer");
      setClientSecret(null);
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/50"
        aria-hidden
        onClick={onClose}
      />
      <div
        className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-card border-l border-border shadow-xl flex flex-col"
        role="dialog"
        aria-label="Cart"
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          {step !== "cart" ? (
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
          ) : (
            <span />
          )}
          <h2 className="font-semibold text-foreground">
            {step === "cart" && "Cart"}
            {step === "customer" && "Your details"}
            {step === "payment" && "Payment"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted text-muted-foreground"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {step === "cart" && (
            <>
              {items.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  Your cart is empty.
                </p>
              ) : (
                <ul className="space-y-3">
                  {items.map((i) => {
                    const stockKey = `${i.productId}:${i.productVariantId ?? ""}`;
                    const maxStock = variantStock[stockKey] ?? null;
                    const atMax = maxStock != null && i.quantity >= maxStock;
                    return (
                    <li
                      key={stockKey}
                      className="flex items-center justify-between gap-2 text-sm border-b border-border pb-3"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-foreground truncate">
                          {i.productName}
                          {i.variantName ? ` · ${i.variantName}` : ""}
                        </p>
                        <p className="text-muted-foreground">
                          £{(i.unitPriceCents / 100).toFixed(2)} each
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => updateQuantity(i.productId, i.productVariantId, i.quantity - 1)}
                          disabled={i.quantity <= 1}
                          className="p-1.5 rounded-lg border border-border bg-background text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                          aria-label={`Decrease quantity of ${i.productName}`}
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-7 text-center font-medium text-foreground tabular-nums">
                          {i.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(i.productId, i.productVariantId, Math.min(i.quantity + 1, maxStock ?? i.quantity + 1))}
                          disabled={atMax}
                          className="p-1.5 rounded-lg border border-border bg-background text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                          aria-label={`Increase quantity of ${i.productName}`}
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <p className="font-medium text-foreground w-14 text-right tabular-nums">
                          £{((i.unitPriceCents * i.quantity) / 100).toFixed(2)}
                        </p>
                        <button
                          type="button"
                          onClick={() => removeItem(i.productId, i.productVariantId)}
                          className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                          aria-label={`Remove ${i.productName}${i.variantName ? ` (${i.variantName})` : ""} from cart`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </li>
                  ); })}
                </ul>
              )}
              {items.length > 0 && (
                <div className="mt-4 flex items-center justify-between text-lg font-semibold">
                  <span className="text-foreground">Total</span>
                  <span className="text-foreground">
                    £{(totalCents / 100).toFixed(2)}
                  </span>
                </div>
              )}
            </>
          )}

          {step === "customer" && (
            <form onSubmit={handleCustomerSubmit} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                You&apos;re signed in. Your order will use the details below.
              </p>
              {checkoutError && (
                <div className="rounded-lg bg-destructive/10 text-destructive text-sm p-3">
                  {checkoutError}
                </div>
              )}
              <div>
                <label htmlFor="cart-email" className="block text-sm font-medium text-foreground mb-1">
                  Email
                </label>
                <input
                  id="cart-email"
                  type="email"
                  required
                  readOnly
                  value={email}
                  className="w-full rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground"
                />
              </div>
              <div>
                <label htmlFor="cart-name" className="block text-sm font-medium text-foreground mb-1">
                  Name (optional)
                </label>
                <input
                  id="cart-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  placeholder="Your name"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Order total: £{(totalCents / 100).toFixed(2)}
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-lg bg-primary py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Preparing…
                  </>
                ) : (
                  "Continue to payment"
                )}
              </button>
            </form>
          )}

          {step === "payment" && clientSecret && (
            <PaymentForm
              clientSecret={clientSecret}
              amountCents={checkoutAmountCents}
              onSuccess={handlePaymentSuccess}
              showSkip={false}
            />
          )}
        </div>

        {step === "cart" && items.length > 0 && (
          <div className="p-4 border-t border-border">
            {acceptsPayments ? (
              <button
                type="button"
                onClick={handleCheckoutClick}
                disabled={checkingAuth}
                className="w-full rounded-lg bg-primary py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {checkingAuth ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Checking…
                  </>
                ) : (
                  "Checkout"
                )}
              </button>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-2">
                Checkout is not available for this business right now.
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export function CartTrigger({
  count,
  onClick,
}: {
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative flex items-center justify-center rounded-lg border border-border bg-card p-2.5 text-foreground hover:bg-muted"
      aria-label={count > 0 ? `Cart (${count} items)` : "Cart"}
    >
      <ShoppingCart className="h-5 w-5" />
      {count > 0 && (
        <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}

/** Client wrapper: cart trigger + drawer open state. Use inside CartProvider. */
export function CartUI({
  businessSlug,
  acceptsPayments,
}: {
  businessSlug: string;
  acceptsPayments: boolean;
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { totalCount } = useCart();
  return (
    <>
      <CartTrigger count={totalCount} onClick={() => setDrawerOpen(true)} />
      <CartDrawer
        businessSlug={businessSlug}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        acceptsPayments={acceptsPayments}
      />
    </>
  );
}
